/**
 * Shared helpers for personal / assigned post-it forms (modal, share, schedule).
 */
import type { UserWithProfile } from '@/types/user.types';
import type {
  AllowedTargetRole,
  AssignedPostIt,
  AssignedRecurrence,
  CreateAssignedPostItDto,
  CreatePostItDto,
  CustomDatesOptions,
  DayOfWeek,
  MonthNumber,
  PostIt,
  PostItColor,
  PostItShare,
  PostItStatus,
  PostItThematic,
  QuarterNumber,
  RecurrenceConfig,
  RecurrenceMode,
  ScheduleConfig,
  ScheduleType,
  UpdatePostItDto,
} from '@/types/post-it.types';
import { postItValidationSchema } from '@/utils/validation-schemas-utils';
import {
  COLORS,
  SCHEDULE_CHIP_ACTIVE_BG_MAP,
  SCHEDULE_CHIP_ACTIVE_BORDER_MAP,
  SCHEDULE_CHIP_ACTIVE_TEXT_MAP,
  SCHEDULE_CHIP_INACTIVE_BORDER_MAP,
  SCHEDULE_CHIP_INACTIVE_TEXT_MAP,
  POST_IT_SCHEDULE_DEFAULT_OPTIONS,
} from '@/components/post-its/post-it-configs';

/** Share payload queued until the post-it is created. */
export type PendingPostItShare = {
  userIds: string[];
  permission: 'read' | 'write';
};

/** Bulk-apply payload: role + legacy recurrence and/or advanced schedule. */
export interface SelectionActionConfig {
  roleId: string;
  recurrence: AssignedRecurrence;
  specificDateFrom?: string;
  specificDateTo?: string;
  quarterlyPeriods?: number[];
  scheduleConfig?: ScheduleConfig;
}

/** Shared title / thematic / content / color / priority fields. */
export interface PersonalPostItFormFields {
  title: string;
  thematic: string;
  content: string;
  color: PostItColor;
  isPriority: boolean;
}

/** Personal-only deadline and status fields. */
export interface PersonalPostItExtraFields {
  deadline: string;
  status: PostItStatus;
}

/** Assigned-variant recurrence, dates, roles, and quarters. */
export interface AssignedFormConfig {
  recurrence: AssignedRecurrence | '';
  specificDateFrom: string;
  specificDateTo: string;
  targetRoleIds: string[];
  quarterlyPeriods: number[];
}

/** Form slices hydrated for the create/edit modal. */
export interface HydratedPostItModalState {
  formData: PersonalPostItFormFields;
  personalData: PersonalPostItExtraFields | null;
  assignConfig: AssignedFormConfig | null;
  scheduleConfig: ScheduleConfig | undefined;
  shouldValidateInitial: boolean;
}

/** Calendar months 1–12 used by the post-it schedule form. */
const ALL_MONTHS: MonthNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Selected vs idle schedule-chip classes for a post-it color. */
export function getScheduleChipClasses(color: PostItColor, selected: boolean): string {
  if (selected) {
    return `${SCHEDULE_CHIP_ACTIVE_BG_MAP[color]} ${SCHEDULE_CHIP_ACTIVE_TEXT_MAP[color]} ${SCHEDULE_CHIP_ACTIVE_BORDER_MAP[color]} font-semibold`;
  }
  return `bg-transparent hover:bg-white/10 ${SCHEDULE_CHIP_INACTIVE_TEXT_MAP[color]} ${SCHEDULE_CHIP_INACTIVE_BORDER_MAP[color]}`;
}

/** Maps new {@link ScheduleType} to legacy {@link AssignedRecurrence} for backward compatibility. */
export function toLegacyRecurrence(type: ScheduleType): AssignedRecurrence {
  switch (type) {
    case 'weekly':
    case 'day_of_week':
      return 'weekly';
    case 'monthly':
    case 'day_of_month':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'school_year':
    case 'civil_year':
      return 'annually';
    case 'custom_dates':
      return 'specific_date';
  }
}

/** Quarterly `targetQuarters` (`'all'` → 1–4) for the legacy API field. */
export function quartersFromSchedule(scheduleConfig: ScheduleConfig | null): number[] {
  if (scheduleConfig?.type !== 'quarterly') return [];
  const target = (scheduleConfig.options as { targetQuarters?: number[] | 'all' } | undefined)
    ?.targetQuarters;
  if (target === 'all') return [1, 2, 3, 4];
  return Array.isArray(target) ? target : [];
}

/** Prefer advanced schedule type; else the legacy recurrence select. */
export function resolveAssignedRecurrence(
  scheduleConfig: ScheduleConfig | null,
  recurrence: string,
): AssignedRecurrence | undefined {
  if (scheduleConfig) return toLegacyRecurrence(scheduleConfig.type);
  return recurrence ? (recurrence as AssignedRecurrence) : undefined;
}

/** Quarters from the advanced schedule, else from the legacy quarterly chips. */
export function resolveAssignedQuarters(
  scheduleConfig: ScheduleConfig | null,
  scheduleQuarters: number[],
  recurrence: string,
  quarterlyPeriods: number[],
): number[] | undefined {
  if (scheduleConfig) {
    return scheduleQuarters.length > 0 ? scheduleQuarters : undefined;
  }
  if (recurrence === 'quarterly' && quarterlyPeriods.length > 0) {
    return quarterlyPeriods;
  }
  return undefined;
}

/** Create vs edit title for personal and assigned variants. */
export function modalDialogTitle(hasExisting: boolean, variant: 'personal' | 'assigned'): string {
  if (hasExisting) return 'Modifier le post-it';
  if (variant === 'assigned') return 'Créer un post-it assigné';
  return 'Créer un post-it';
}

/** Date or ISO string → ISO string; empty when unset. */
export function normalizeDeadline(deadline?: string | Date | null): string {
  if (!deadline) return '';
  if (typeof deadline === 'string') return deadline;
  return deadline.toISOString();
}

/** Assigned submit: schedule config, or a specific date when that recurrence is chosen. */
export function isAssignValid(
  scheduleConfig: ScheduleConfig | null,
  recurrence: AssignedRecurrence | '',
  specificDateFrom: string,
): boolean {
  if (scheduleConfig) return true;
  if (recurrence === 'specific_date' && !specificDateFrom) return false;
  return true;
}

/** Deadline hours (personal) or assigned recurrence; hook `validateForm` stays in the modal. */
export function getModalSubmitError(
  variant: 'personal' | 'assigned',
  form: PersonalPostItFormFields,
  personal: PersonalPostItExtraFields,
  schedule: ScheduleConfig | null,
  assign: AssignedFormConfig,
  isFormValid: boolean,
): string | undefined {
  if (!isFormValid) {
    if (variant === 'personal') {
      const dataToValidate = { ...form, deadline: personal.deadline };
      const deadlineError = postItValidationSchema.deadline?.custom?.(
        personal.deadline,
        dataToValidate,
      );
      return deadlineError || 'requiredFields';
    }
    return 'requiredFields';
  }
  if (
    variant === 'assigned' &&
    !isAssignValid(schedule, assign.recurrence, assign.specificDateFrom)
  ) {
    return 'requiredRecurrenceDates';
  }
  return undefined;
}

/** True when title/thematic/content are empty, assign is invalid, or a save is in flight. */
export function isPostItSubmitDisabled(
  formData: PersonalPostItFormFields,
  variant: 'personal' | 'assigned',
  loading: boolean,
  scheduleConfig: ScheduleConfig | null,
  assignConfig: AssignedFormConfig,
): boolean {
  const baseInvalid = !formData.title.trim() || !formData.thematic || !formData.content.trim();
  if (variant === 'assigned') {
    return (
      baseInvalid ||
      !isAssignValid(scheduleConfig, assignConfig.recurrence, assignConfig.specificDateFrom) ||
      loading
    );
  }
  return baseInvalid || loading;
}

/** Palette plus legacy `blanc-casse` when editing a post-it that still uses it. */
export function getAvailablePostItColors(
  mode: 'create' | 'edit' | 'view',
  currentColor: PostItColor,
): PostItColor[] {
  const colors = [...COLORS];
  if (mode === 'edit' && currentColor === 'blanc-casse' && !colors.includes('blanc-casse')) {
    colors.push('blanc-casse');
  }
  return colors;
}

/** Create-assigned DTO; prefer advanced schedule over legacy recurrence. */
export function buildAssignedPayload(
  formData: PersonalPostItFormFields,
  assignConfig: AssignedFormConfig,
  scheduleConfig: ScheduleConfig | null,
): CreateAssignedPostItDto {
  const useSpecificDates = !scheduleConfig && assignConfig.recurrence === 'specific_date';
  return {
    title: formData.title.trim(),
    thematic: formData.thematic as PostItThematic,
    content: formData.content.trim(),
    color: formData.color,
    isPriority: formData.isPriority,
    scheduleConfig: scheduleConfig || undefined,
    recurrence: resolveAssignedRecurrence(scheduleConfig, assignConfig.recurrence),
    specificDateFrom: useSpecificDates ? assignConfig.specificDateFrom : undefined,
    specificDateTo: useSpecificDates ? assignConfig.specificDateTo : undefined,
    targetRoleIds: assignConfig.targetRoleIds.length > 0 ? assignConfig.targetRoleIds : undefined,
    quarterlyPeriods: resolveAssignedQuarters(
      scheduleConfig,
      quartersFromSchedule(scheduleConfig),
      assignConfig.recurrence,
      assignConfig.quarterlyPeriods,
    ),
  };
}

/** Create/update personal DTO; status is only sent on edit. */
export function buildPersonalPayload(
  formData: PersonalPostItFormFields,
  personalData: PersonalPostItExtraFields,
  mode: 'create' | 'edit' | 'view',
): CreatePostItDto | UpdatePostItDto {
  const base = {
    title: formData.title.trim(),
    thematic: formData.thematic as PostItThematic,
    content: formData.content.trim(),
    deadline: personalData.deadline || undefined,
    color: formData.color,
    isPriority: formData.isPriority,
  };
  return mode === 'create' ? base : { ...base, status: personalData.status };
}

/** Placeholder {@link PostIt} so the share modal can open before POST. */
export function buildShareModalPlaceholder(formData: PersonalPostItFormFields): PostIt {
  return {
    id: '',
    companyId: '',
    createdById: '',
    createdByName: '',
    title: formData.title.trim() || 'Nouveau post-it',
    thematic: (formData.thematic || 'autres') as PostItThematic,
    content: formData.content,
    deadline: null,
    status: 'en_cours',
    color: formData.color,
    isArchived: false,
    isPriority: formData.isPriority,
    visibleFrom: null,
    createdAt: new Date(),
    updatedAt: new Date(),
    userPermission: 'owner',
    shares: [],
  };
}

/** Allowed roles plus a fallback label if the saved role is from the other mode. */
export function buildTargetRoleOptions(
  allowedRoles: { id: string; label: string }[] | undefined,
  selectedId: string | undefined,
): { value: string; label: string }[] {
  const options = (allowedRoles ?? []).map((r) => ({
    value: r.id,
    label: r.label,
  }));
  if (selectedId && !options.some((o) => o.value === selectedId)) {
    options.push({ value: selectedId, label: 'Rôle (autre mode)' });
  }
  return options;
}

/** Default schedule options + indefinite recurrence for a schedule type. */
export function buildDefaultScheduleConfig(type: ScheduleType): ScheduleConfig {
  return {
    type,
    options: POST_IT_SCHEDULE_DEFAULT_OPTIONS[type] as ScheduleConfig['options'],
    recurrence: { mode: 'indefinitely' },
  };
}

/** Toggle one month; 12 selected months collapse to `'all'`. */
export function toggleTargetMonth(
  current: MonthNumber[] | 'all',
  month: MonthNumber,
): MonthNumber[] | 'all' {
  const list = current === 'all' ? ALL_MONTHS : current;
  const updated = list.includes(month) ? list.filter((m) => m !== month) : [...list, month];
  return updated.length === 12 ? 'all' : updated;
}

/** Flip months between `'all'` and none. */
export function toggleAllMonths(current: MonthNumber[] | 'all'): MonthNumber[] | 'all' {
  return current === 'all' ? [] : 'all';
}

/** True when the month is in the list or the list is `'all'`. */
export function isMonthActive(targetMonths: MonthNumber[] | 'all', month: MonthNumber): boolean {
  return targetMonths === 'all' || targetMonths.includes(month);
}

/** Toggle one quarter; 4 selected quarters collapse to `'all'`. */
export function toggleTargetQuarter(
  current: QuarterNumber[] | 'all',
  quarter: QuarterNumber,
): QuarterNumber[] | 'all' {
  const list = current === 'all' ? [] : current;
  const updated = list.includes(quarter) ? list.filter((q) => q !== quarter) : [...list, quarter];
  return updated.length === 4 ? 'all' : updated;
}

/** Flip quarters between `'all'` and none. */
export function toggleAllQuarters(current: QuarterNumber[] | 'all'): QuarterNumber[] | 'all' {
  return current === 'all' ? [] : 'all';
}

/** True when the quarter is in the list or the list is `'all'`. */
export function isQuarterActive(
  targetQuarters: QuarterNumber[] | 'all',
  quarter: QuarterNumber,
): boolean {
  return targetQuarters === 'all' || targetQuarters.includes(quarter);
}

/** Add or drop a weekday from the recurrence list. */
export function toggleDayOfWeek(current: DayOfWeek[] | undefined, day: DayOfWeek): DayOfWeek[] {
  const list = current || [];
  return list.includes(day) ? list.filter((d) => d !== day) : [...list, day];
}

/** Append a 2020–2100 year; undefined when the raw value is invalid. */
export function addSelectedYear(
  selectedYears: number[] | undefined,
  raw: string,
): number[] | undefined {
  const year = Number.parseInt(raw, 10);
  if (year < 2020 || year > 2100) return undefined;
  const current = selectedYears || [];
  if (current.includes(year)) return current;
  return [...current, year].sort((a, b) => a - b);
}

/** Drop one year from the selected list. */
export function removeSelectedYear(selectedYears: number[] | undefined, year: number): number[] {
  return (selectedYears || []).filter((y) => y !== year);
}

/** Switch recurrence mode and clear count / picked keys. */
export function changeRecurrenceMode(
  value: RecurrenceConfig,
  mode: RecurrenceMode,
): RecurrenceConfig {
  return { ...value, mode, count: undefined, pickedKeys: undefined };
}

/** Add or drop one occurrence key on a custom recurrence. */
export function togglePickedOccurrenceKey(value: RecurrenceConfig, key: string): RecurrenceConfig {
  const current = value.pickedKeys || [];
  const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
  return { ...value, pickedKeys: updated };
}

/** Deduped allowed-target roles for the current app mode. */
export function dedupeShareableRoles(allowedRoles: AllowedTargetRole[]): AllowedTargetRole[] {
  const byCode = new Map<string, AllowedTargetRole>();
  for (const role of allowedRoles) {
    const key = role.code.toLowerCase();
    if (!byCode.has(key)) byCode.set(key, role);
  }
  return [...byCode.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

/** `'all'` → every code; otherwise a one-item filter. */
export function resolveShareRoleCodes(roleFilter: string, allCodes: string[]): string[] {
  if (roleFilter === 'all') return allCodes;
  return [roleFilter];
}

/** True when the filter is `'all'` or matches a shareable role. */
export function isShareRoleFilterValid(
  roleFilter: string,
  shareableRoles: AllowedTargetRole[],
): boolean {
  if (roleFilter === 'all') return true;
  return shareableRoles.some((r) => r.code.toLowerCase() === roleFilter.toLowerCase());
}

/** First + last initial, or `'?'` when both names are empty. */
export function getShareUserInitials(firstName?: string | null, lastName?: string | null): string {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
}

/** Selected ids that are not already shared (deduped). */
export function filterNewShareUserIds(
  selectedUsers: string[],
  alreadySharedIds: string[],
): string[] {
  return [...new Set(selectedUsers.filter((id) => !alreadySharedIds.includes(id)))];
}

/** Add or drop a user id from the share selection. */
export function toggleSelectedUserId(selected: string[], userId: string): string[] {
  if (selected.includes(userId)) return selected.filter((id) => id !== userId);
  return [...selected, userId];
}

/** Select/deselect every id on the current page. */
export function mergePageSelection(
  selected: string[],
  pageIds: string[],
  allSelected: boolean,
): string[] {
  if (allSelected) return selected.filter((id) => !pageIds.includes(id));
  return [...new Set([...selected, ...pageIds])];
}

/** Keep profile objects for currently selected user ids. */
export function mergeSelectedUsersMap(
  prev: Record<string, UserWithProfile>,
  users: UserWithProfile[],
  selectedUsers: string[],
): Record<string, UserWithProfile> {
  const next = { ...prev };
  for (const user of users) {
    if (selectedUsers.includes(user.id)) next[user.id] = user;
  }
  return next;
}

/** Bulk-apply needs a selection, a role, and a valid schedule or recurrence. */
export function isSelectionApplyEnabled(
  hasSelection: boolean,
  roleId: string,
  scheduleConfig: ScheduleConfig | null,
  recurrence: AssignedRecurrence | '',
  specificDateFrom: string,
): boolean {
  return (
    hasSelection &&
    !!roleId &&
    (!!scheduleConfig || (!!recurrence && (recurrence !== 'specific_date' || !!specificDateFrom)))
  );
}

/** Bulk-apply payload: role + legacy recurrence and/or advanced schedule. */
export function buildSelectionActionConfig(
  roleId: string,
  scheduleConfig: ScheduleConfig | null,
  recurrence: AssignedRecurrence | '',
  specificDateFrom: string,
  specificDateTo: string,
  quarterlyPeriods: number[],
): SelectionActionConfig {
  let scheduleQuarters: number[] = [];
  if (scheduleConfig?.type === 'quarterly') {
    const target = (scheduleConfig.options as { targetQuarters?: number[] | 'all' } | undefined)
      ?.targetQuarters;
    if (target === 'all') {
      scheduleQuarters = [1, 2, 3, 4];
    } else if (Array.isArray(target)) {
      scheduleQuarters = target;
    }
  }

  let applyRecurrence: AssignedRecurrence = 'weekly';
  if (scheduleConfig) {
    applyRecurrence = toLegacyRecurrence(scheduleConfig.type);
  } else if (recurrence) {
    applyRecurrence = recurrence;
  }

  let applyQuarters: number[] | undefined;
  if (scheduleConfig) {
    applyQuarters = scheduleQuarters.length > 0 ? scheduleQuarters : undefined;
  } else if (recurrence === 'quarterly') {
    applyQuarters = quarterlyPeriods;
  }

  return {
    roleId,
    scheduleConfig: scheduleConfig || undefined,
    recurrence: applyRecurrence,
    specificDateFrom:
      !scheduleConfig && recurrence === 'specific_date' ? specificDateFrom : undefined,
    specificDateTo: !scheduleConfig && recurrence === 'specific_date' ? specificDateTo : undefined,
    quarterlyPeriods: applyQuarters,
  };
}

/** Narrow a year-scope select value to the custom-dates union. */
export function asCustomYearScope(value: string): CustomDatesOptions['yearScope'] {
  return value as CustomDatesOptions['yearScope'];
}

/** Unwrap `{ shares }` or a bare array; else the fallback. */
export function resolveExistingShares(
  sharesResponse: { shares?: PostItShare[] } | PostItShare[] | undefined,
  fallback: PostItShare[] | undefined,
): PostItShare[] {
  if (
    sharesResponse &&
    typeof sharesResponse === 'object' &&
    'shares' in sharesResponse &&
    Array.isArray(sharesResponse.shares)
  ) {
    return sharesResponse.shares;
  }
  if (Array.isArray(sharesResponse)) return sharesResponse;
  return fallback ?? [];
}

/** Hydrate personal / assigned modal fields from GET post-it. */
export function hydratePostItModalState(
  variant: 'personal' | 'assigned',
  postIt?: PostIt,
  assignedPostIt?: AssignedPostIt,
): HydratedPostItModalState {
  const formData: PersonalPostItFormFields = {
    title: (assignedPostIt?.title ?? postIt?.title) || '',
    thematic: (assignedPostIt?.thematic ?? postIt?.thematic) || '',
    content: (assignedPostIt?.content ?? postIt?.content) || '',
    color: ((assignedPostIt?.color ?? postIt?.color) || 'jaune') as PostItColor,
    isPriority: (assignedPostIt?.isPriority ?? postIt?.isPriority) || false,
  };

  if (variant === 'personal') {
    const personalData: PersonalPostItExtraFields = {
      deadline: normalizeDeadline(postIt?.deadline),
      status: (postIt?.status || 'en_cours') as PostItStatus,
    };
    return {
      formData,
      personalData,
      assignConfig: null,
      scheduleConfig: undefined,
      shouldValidateInitial: Boolean(postIt),
    };
  }

  const assignConfig: AssignedFormConfig = assignedPostIt
    ? {
        recurrence: (assignedPostIt.recurrence as AssignedRecurrence) || '',
        specificDateFrom: assignedPostIt.specificDateFrom || '',
        specificDateTo: assignedPostIt.specificDateTo || '',
        targetRoleIds: assignedPostIt.targetRoleIds ?? [],
        quarterlyPeriods: assignedPostIt.quarterlyPeriods ?? [],
      }
    : {
        recurrence: '',
        specificDateFrom: '',
        specificDateTo: '',
        targetRoleIds: [],
        quarterlyPeriods: [],
      };

  const scheduleConfig =
    assignedPostIt && 'scheduleConfig' in assignedPostIt && assignedPostIt.scheduleConfig
      ? assignedPostIt.scheduleConfig
      : undefined;

  return {
    formData,
    personalData: null,
    assignConfig,
    scheduleConfig,
    shouldValidateInitial: false,
  };
}
